import { useMemo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculateFeatureWTP } from '../../lib/calculations';
import { formatPrice } from '../../lib/formatters';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, CHART_COLORS } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';
import { useIsMobile } from '../../hooks/useIsMobile';

const PKG_COLORS = [CHART_COLORS.teal, CHART_COLORS.blue, CHART_COLORS.amber, CHART_COLORS.purple, CHART_COLORS.pink];

export function PriceValueMap() {
  const isMobile = useIsMobile();
  const packages = useSimulatorStore(s => s.packages);
  const assumptions = useSimulatorStore(s => s.assumptions);

  const data = useMemo(() => {
    return packages.map((pkg, i) => {
      const totalWTP = pkg.enabledFeatures.reduce((sum, f) => {
        return sum + calculateFeatureWTP(f, pkg.price, assumptions);
      }, 0);
      return {
        name: pkg.name,
        price: pkg.price,
        totalWTP: Math.round(totalWTP),
        features: pkg.enabledFeatures.length,
        color: PKG_COLORS[i % PKG_COLORS.length],
      };
    });
  }, [packages, assumptions]);

  const maxPrice = Math.max(...data.map(d => d.price), 2000);
  const maxWTP = Math.max(...data.map(d => d.totalWTP), 1500);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Price vs. Perceived Value</h3>
        <InfoButton helpKey="priceValueMap" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">
        Each dot is a configured package. X = S2 price, Y = total feature WTP (sum of implied dollar values). Packages above the diagonal offer more perceived value than their price.
      </p>

      <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
        <ScatterChart margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
          <CartesianGrid {...GRID_PROPS} vertical />
          <XAxis type="number" dataKey="price" name="Price" {...X_AXIS_PROPS} tickFormatter={v => formatPrice(v)} domain={[0, maxPrice + 200]} label={{ value: 'S2 Price →', position: 'bottom', offset: 0, fill: 'var(--color-text-muted)', fontSize: 11 }} />
          <YAxis type="number" dataKey="totalWTP" name="Total WTP" {...Y_AXIS_PROPS} tickFormatter={v => formatPrice(v)} domain={[0, maxWTP + 200]} label={{ value: '← Total Feature WTP', angle: -90, position: 'insideLeft', offset: 10, fill: 'var(--color-text-muted)', fontSize: 11 }} />
          <Tooltip
            {...TOOLTIP_STYLE}
            formatter={(value: number, name: string) => [formatPrice(value), name === 'price' ? 'S2 Price' : 'Total WTP']}
            labelFormatter={() => ''}
            content={({ payload }) => {
              if (!payload || payload.length === 0) return null;
              const d = payload[0]?.payload;
              if (!d) return null;
              return (
                <div style={{ ...TOOLTIP_STYLE.contentStyle, padding: '10px 14px' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--color-text-primary)' }}>{d.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Price: {formatPrice(d.price)}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Total WTP: {formatPrice(d.totalWTP)}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Features: {d.features}/7</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: d.totalWTP >= d.price ? '#059669' : '#DC2626', marginTop: 4 }}>
                    {d.totalWTP >= d.price ? 'Good value (WTP > Price)' : 'Overpriced (Price > WTP)'}
                  </div>
                </div>
              );
            }}
          />
          <ReferenceLine segment={[{ x: 0, y: 0 }, { x: maxPrice + 200, y: maxPrice + 200 }]} stroke="var(--color-reference-line)" strokeDasharray="6 3" strokeWidth={1.5} />
          {data.map((d, i) => (
            <Scatter key={i} name={d.name} data={[d]} fill={d.color} fillOpacity={0.9} r={10} />
          ))}
        </ScatterChart>
      </ResponsiveContainer>

      <div className="flex flex-wrap items-center gap-4 mt-3 text-micro text-muted">
        <span>Diagonal = Price equals WTP</span>
        <span>Above diagonal = good value</span>
        <span>Below diagonal = overpriced</span>
        {data.map((d, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
            {d.name}
          </span>
        ))}
      </div>
    </div>
  );
}
